import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardList,
  TrendingUp,
  Target,
  Apple,
  Flame,
  Star,
  Eye,
  CheckCircle,
  Bell,
  MessageSquare,
  User,
} from "lucide-react";
import { API } from "@/App";
import { formatDateLongTR } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

// Category icons
const CATEGORY_ICONS = {
  general: ClipboardList,
  progress: TrendingUp,
  technique: Target,
  nutrition: Apple,
  motivation: Flame,
};

const CATEGORY_COLORS = {
  blue: "bg-blue-100 text-blue-700",
  green: "bg-green-100 text-green-700",
  purple: "bg-purple-100 text-purple-700",
  orange: "bg-orange-100 text-orange-700",
  red: "bg-red-100 text-red-700",
};

export default function MyEvaluationsModule({ businessId, user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "evaluations"]);

  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchEvaluations();
      fetchUnreadCount();
    }
  }, [businessId]);

  const fetchEvaluations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(
        `${API}/evaluations/member?business_id=${businessId}`,
        { credentials: "include", headers }
      );
      if (response.ok) {
        const data = await response.json();
        setEvaluations(data);
      }
    } catch (error) {
      console.error("Error fetching evaluations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(
        `${API}/evaluations/member/unread-count?business_id=${businessId}`,
        { credentials: "include", headers }
      );
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const handleViewEvaluation = async (evaluation) => {
    setSelectedEvaluation(evaluation);
    setShowDetailModal(true);

    // Mark as read if unread
    if (!evaluation.is_read) {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        await fetch(
          `${API}/evaluations/member/${evaluation.evaluation_id}/read`,
          {
            method: "POST",
            credentials: "include",
            headers,
          }
        );
        // Update local state
        setEvaluations((prev) =>
          prev.map((e) =>
            e.evaluation_id === evaluation.evaluation_id
              ? { ...e, is_read: true }
              : e
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Error marking as read:", error);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(
        `${API}/evaluations/member/mark-all-read?business_id=${businessId}`,
        {
          method: "POST",
          credentials: "include",
          headers,
        }
      );
      if (response.ok) {
        toast.success(t('common:allEvaluationsMarkedRead'));
        setEvaluations((prev) => prev.map((e) => ({ ...e, is_read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const getIconComponent = (category) => {
    return CATEGORY_ICONS[category] || ClipboardList;
  };

  const getColorClass = (color) => {
    return CATEGORY_COLORS[color] || CATEGORY_COLORS.blue;
  };

  const formatDate = (dateStr) => {
    return formatDateLongTR(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            Değerlendirmelerim
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white">{unreadCount} yeni</Badge>
            )}
          </h1>
          <p className="text-grey text-sm">
            Antrenörlerinizden gelen geri bildirimler
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            onClick={handleMarkAllRead}
            className="rounded-xl"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Tümünü Okundu İşaretle
          </Button>
        )}
      </div>

      {/* Evaluations List */}
      {evaluations.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-grey/50 mb-4" />
            <p className="text-grey">{t("evaluations:henuzDegerlendirmeAlmadiniz")}</p>
            <p className="text-sm text-grey mt-1">
              Antrenörlerinizden gelen geri bildirimler burada görünecek
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {evaluations.map((evaluation) => {
            const IconComp = getIconComponent(evaluation.category);
            const isUnread = !evaluation.is_read;

            return (
              <Card
                key={evaluation.evaluation_id}
                className={`border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                  isUnread ? "ring-2 ring-blue-500 ring-offset-2" : ""
                }`}
                onClick={() => handleViewEvaluation(evaluation)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div
                      className={`p-3 rounded-xl ${getColorClass(
                        evaluation.category_color
                      )}`}
                    >
                      <IconComp className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-navy">
                          {evaluation.title}
                        </h3>
                        <Badge
                          className={`${getColorClass(
                            evaluation.category_color
                          )} text-xs`}
                        >
                          {evaluation.category_name}
                        </Badge>
                        {isUnread && (
                          <Badge className="bg-blue-500 text-white text-xs">
                            <Bell className="w-3 h-3 mr-1" />
                            Yeni
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-grey mt-1 line-clamp-2">
                        {evaluation.content}
                      </p>
                      {evaluation.rating && (
                        <div className="flex items-center gap-1 mt-2">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i <= evaluation.rating
                                  ? "text-amber-500 fill-amber-500"
                                  : "text-slate-300"
                              }`}
                            />
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-grey">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {evaluation.trainer_name}
                        </span>
                        <span>{formatDate(evaluation.created_at)}</span>
                      </div>
                    </div>
                    <Eye className="w-5 h-5 text-grey flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('common:evaluationDetail')}</DialogTitle>
          </DialogHeader>
          {selectedEvaluation && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                {(() => {
                  const IconComp = getIconComponent(selectedEvaluation.category);
                  return (
                    <div
                      className={`p-3 rounded-xl ${getColorClass(
                        selectedEvaluation.category_color
                      )}`}
                    >
                      <IconComp className="w-6 h-6" />
                    </div>
                  );
                })()}
                <div>
                  <h3 className="font-semibold text-navy text-lg">
                    {selectedEvaluation.title}
                  </h3>
                  <Badge
                    className={`${getColorClass(
                      selectedEvaluation.category_color
                    )} mt-1`}
                  >
                    {selectedEvaluation.category_name}
                  </Badge>
                </div>
              </div>

              <div className="p-4 bg-white border rounded-xl">
                <p className="text-navy whitespace-pre-wrap">
                  {selectedEvaluation.content}
                </p>
              </div>

              {selectedEvaluation.rating && (
                <div className="p-3 bg-amber-50 rounded-xl">
                  <p className="text-xs text-amber-600 mb-1">{t('business:rating')}</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        className={`w-6 h-6 ${
                          i <= selectedEvaluation.rating
                            ? "text-amber-500 fill-amber-500"
                            : "text-slate-300"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="p-3 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800 font-medium">
                    {selectedEvaluation.trainer_name}
                  </span>
                  {selectedEvaluation.trainer_role && (
                    <span className="text-xs text-blue-600">
                      ({selectedEvaluation.trainer_role})
                    </span>
                  )}
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  {formatDate(selectedEvaluation.created_at)}
                </p>
              </div>

              <Button
                variant="outline"
                onClick={() => setShowDetailModal(false)}
                className="w-full rounded-xl"
              >
                Kapat
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
